import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Monitor, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface TrustedDevice {
  id: number;
  deviceLabel: string | null;
  ipAddress: string | null;
  lastUsedAt: string | null;
  createdAt: string | null;
  expiresAt: string;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  return `${Math.floor(ms / 86400_000)}d ago`;
}

function formatExpires(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function truncateLabel(label: string | null): string {
  if (!label) return "Unknown device";
  return label.length > 60 ? label.slice(0, 60) + "…" : label;
}

export default function TrustedDevicesList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<TrustedDevice | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading } = useQuery<{ devices: TrustedDevice[] }>({
    queryKey: ["/api/auth/2fa/trusted-devices"],
    queryFn: async () => {
      const res = await fetch("/api/auth/2fa/trusted-devices", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load trusted devices");
      return res.json();
    },
  });

  async function confirmRevoke() {
    if (!revoking) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/auth/2fa/trusted-devices/${revoking.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast({
          title: "Device revoked",
          description: "Next login from this device will require a 2FA code.",
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/auth/2fa/trusted-devices"],
        });
        setRevoking(null);
      } else {
        toast({ title: "Revoke failed", variant: "destructive" });
      }
    } catch {
      toast({ title: "Network error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading trusted devices...
      </div>
    );
  }

  const devices = data?.devices ?? [];

  if (devices.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-3">
        No trusted devices. When you sign in with "Remember this device," the
        device appears here.
      </p>
    );
  }

  return (
    <>
      <div className="border rounded divide-y dark:divide-gray-700">
        {devices.map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between p-3 text-sm"
          >
            <div className="flex items-start gap-3 min-w-0">
              <Monitor className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate">{truncateLabel(d.deviceLabel)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {d.ipAddress ?? "unknown IP"} · last used {formatRelative(d.lastUsedAt)} ·
                  expires {formatExpires(d.expiresAt)}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevoking(d)}
              aria-label="Revoke device"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <AlertDialog
        open={revoking !== null}
        onOpenChange={(o) => {
          if (!o) setRevoking(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this device?</AlertDialogTitle>
            <AlertDialogDescription>
              Future logins from this device will require a 2FA code. This
              action is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              disabled={submitting}
            >
              {submitting ? "Revoking..." : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
